import { BookOpen, ExternalLink, Link2, Lock } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { clerkEnabled } from "@/services/clerk";
import { useMe } from "@/features/members/hooks/useMe";
import { useReply, useThread } from "@/features/discussions/discussions";
import type { AuthorSummary } from "@/features/discussions/discussions";
import { SuggestResourceDialog } from "@/components/SuggestResourceDialog";
import { ThreadSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

function AuthorLine({
  author,
  time,
}: {
  author: AuthorSummary;
  time: string;
}) {
  const name = (
    <span className="font-medium underline-offset-4 hover:underline">
      {author.name}
    </span>
  );
  return (
    <div className="flex items-center gap-2">
      <Avatar className="size-8">
        <AvatarImage src={author.image_url ?? undefined} alt={author.name} />
        <AvatarFallback>{author.name.charAt(0)}</AvatarFallback>
      </Avatar>
      <div className="text-sm">
        {author.id ? <Link to={`/directory/${author.id}`}>{name}</Link> : name}
        <span className="text-muted-foreground">
          {author.org ? ` · ${author.org}` : ""} · {timeAgo(time)}
        </span>
      </div>
    </div>
  );
}

export default function ThreadPage() {
  const { id } = useParams();
  const { data: me } = useMe(clerkEnabled);
  const { data: thread, isPending, error } = useThread(id);
  const reply = useReply(id);
  const [body, setBody] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [prefillUrl, setPrefillUrl] = useState<string | undefined>();
  const isStaff = Boolean(me?.member && me.member.role !== "member");

  function handleReply() {
    reply.mutate(body, {
      onSuccess: (result) => {
        setBody("");
        if (result.status === "pending") {
          toast.success("Reply submitted. It will appear once approved");
        }
      },
      onError: (err) => toast.error(err.message),
    });
  }

  if (isPending) return <ThreadSkeleton />;
  if (error || !thread) {
    return (
      <p className="text-muted-foreground mt-12 text-center">
        This discussion isn&apos;t available.
      </p>
    );
  }

  return (
    <div className="animate-page-enter max-w-5xl">
      {/* Sidebar nav is the way back; see MemberProfilePage. */}
      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl">{thread.title}</h1>
            {thread.status === "pending" && (
              <Badge variant="outline">Awaiting review</Badge>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {thread.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>

          <Card className="mt-4">
            <CardContent className="space-y-3">
              <AuthorLine author={thread.author} time={thread.created_at} />
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {thread.body}
              </p>
            </CardContent>
          </Card>

          <div className="mt-4 space-y-3">
            {thread.posts.map((p) => (
              <Card
                key={p.id}
                className={cn(
                  "animate-item-enter",
                  p.status === "pending" && "border-dashed"
                )}
              >
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <AuthorLine author={p.author} time={p.created_at} />
                    {p.status === "pending" && (
                      <Badge variant="outline">Awaiting review</Badge>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {p.body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {thread.status === "published" && (
            <Card className="mt-6">
              <CardContent className="space-y-3">
                <Textarea
                  rows={4}
                  placeholder={
                    isStaff
                      ? "Write a reply…"
                      : "Write a reply… (replies are reviewed before they go live)"
                  }
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleReply}
                    disabled={!body.trim() || reply.isPending}
                  >
                    {reply.isPending && <Spinner data-slot="icon" />}
                    {reply.isPending
                      ? "Sending…"
                      : isStaff
                        ? "Reply"
                        : "Submit reply"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle
                className={`flex items-center gap-2 ${sectionTitleClass}`}
              >
                <BookOpen className="size-4" /> Linked resources
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {thread.linked_resources.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No resources linked to this discussion yet.
                </p>
              ) : (
                thread.linked_resources.map((r) => (
                  <div key={r.id} className="text-sm">
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 underline-offset-4 hover:underline"
                    >
                      {r.access === "paid" && <Lock className="size-3" />}
                      {r.title}
                      <ExternalLink className="size-3" />
                    </a>
                    <Badge variant="secondary" className="mt-0.5 text-xs">
                      {r.category}
                    </Badge>
                  </div>
                ))
              )}
              <Button
                variant="outline"
                size="sm"
                className="mt-2 w-full"
                onClick={() => {
                  setPrefillUrl(undefined);
                  setSuggestOpen(true);
                }}
              >
                Suggest a resource from this thread
              </Button>
            </CardContent>
          </Card>

          {thread.mentioned_links.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle
                  className={`flex items-center gap-2 ${sectionTitleClass}`}
                >
                  <Link2 className="size-4" /> Links mentioned
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {thread.mentioned_links.map((url) => (
                  <div key={url} className="text-sm">
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground block truncate underline-offset-4 hover:underline"
                    >
                      {url}
                    </a>
                    <button
                      className="text-primary text-xs underline-offset-4 hover:underline"
                      onClick={() => {
                        setPrefillUrl(url);
                        setSuggestOpen(true);
                      }}
                    >
                      Save to resource library →
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <SuggestResourceDialog
        key={prefillUrl ?? "no-prefill"}
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        threadId={thread.id}
        prefillUrl={prefillUrl}
      />
    </div>
  );
}
